/** @jsxImportSource @emotion/react */
import copy from '../assets/icons/copyOutline.svg';

import * as React from 'react';
import { css, SerializedStyles } from '@emotion/react';
import { Row } from '../layout/flexbox';
import { IKON_BLUE } from '../styles/colors';
import TooltipView from '../layout/tooltip/TooltipView';
import { doNothing, assoc } from '../language/module';
import { CLEAR_BUTTON, TOOLTIP, XLargeText } from '../styles/styles';
import { connect } from 'react-redux';
import { copyToClipboardAction } from './copyToClipboardAction';
import { getResource } from '../language/resources/getResource';
import { IAppState } from '../core/redux/IAppState';

const copyImage = css`
  width: 17px;
  height: 17px;
  margin-bottom: -2px; /* Align with text on the side */ 
`;

type Props = {
  textToCopy: string,
  title?: string,
  buttonTitle?: string,
  rootCss?: SerializedStyles,
  innerCss?: SerializedStyles,
  buttonCss?: SerializedStyles,
  tooltipDuration?: number,
  resources: Record<string, any>,
  copyToClipboard?: (textToCopy: string) => void,
};

/**
 * CopyToClipboardButton
 * Displays a clickable icon (essentially a button) that copies the supplied prop to the system's clipboard.
 *
 * It creates a text field element. This should preserve any line breaks in the string if it's a complex string. It
 * then sets the text to textToCopy and adds the text field to the body. Next, it selects and copies the string to the
 * clipboard, and then finally, the component removes the temporary text field element.
 *
 * @param textToCopy        - The text to copy to the clipboard
 * @param title             - The title (tooltip) of the span box displaying the text to be copied
 * @param buttonTitle       - The title (tooltip) displayed for the copy to clipboard icon
 * @param css         - Optional. Passes down the css for styling, which is applied to the Row that
 *                            surrounds the input and button. Default: ''
 * @param innerCss    - Optional. Passes down to the css for the span element used to display the
 *                            textToCopy. Default: ''
 * @param buttonCss   - Optional. Passes down to the css of the Copy to Clipboard button. Default: ''
 * @param tooltipDuration   - Optional. How long (in ms) the tooltip should display for. Default: 3000
 */

export default connect(
  (state: IAppState) => ({ resources: state.locale }),
  (dispatch) => ({
    copyToClipboard: (textToCopy: string) => dispatch(copyToClipboardAction(textToCopy, '')),
  })
)
(class CopyToClipboardButton extends React.PureComponent<Props, {
  showTooltip: boolean,
}> {
  public static defaultProps = {
    tooltipDuration: 3000,
  };

  public state = {
    showTooltip: false,
  };

  private tooltip = <span css={css`${TOOLTIP}; width: auto;`}>{getResource(this.props.resources, 'COPIED_TO_CLIPBOARD')}</span>;
  private timer: number | undefined;

  private onCopyClick = () => {
    this.props.copyToClipboard!(this.props.textToCopy);

    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = undefined;
    }

    this.setState(prev => assoc('showTooltip', true, prev));

    this.timer = window.setTimeout(
      () => this.setState(prev => assoc('showTooltip', false, prev)),
      this.props.tooltipDuration,
    );
  };


  public render = () => (
    <Row alignItems='center' rootCss={this.props.rootCss}>
      <span title={this.props.title || this.props.textToCopy}
            css={css`
              ${XLargeText};
              color: ${IKON_BLUE};
              ${this.props.innerCss};
            `}>
        {this.props.textToCopy}
      </span>
      <TooltipView showTooltip={this.state.showTooltip}
                    side='top'
                    tooltipContent={this.tooltip}
                    activateOnHover={false}
                    onClickOutside={doNothing}
                    onClickInside={doNothing}>
        <button type='button'
                title={this.props.buttonTitle || getResource(this.props.resources, 'COPY_TO_CLIPBOARD')}
                onClick={this.onCopyClick}
                css={css`
                  pointer-events: initial;
                  ${this.props.buttonCss};
                  ${CLEAR_BUTTON};
                `}>
          <img src={copy}
               alt='Copy'
               css={copyImage}/>
        </button>
      </TooltipView>
    </Row>
  );
});
